'use client'

import { useEffect } from 'react'
import { Geist } from 'next/font/google'
import Link from 'next/link'

const geistSans = Geist({
  variable: '--font-geist-sans',
  subsets: ['latin'],
})

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Defer PostHog import to avoid blocking error page render (bundle-defer-third-party)
  useEffect(() => {
    import('posthog-js').then((posthog) => {
      posthog.default.captureException(error)
    })
  }, [error])

  return (
    <html lang="en">
      <body className={`${geistSans.variable} font-sans antialiased`}>
        <div className="flex min-h-screen flex-col items-center justify-center bg-linear-to-b from-white via-purple-500/5 to-blue-500/5 p-4">
          {/* Background decorations */}
          <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
            <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-purple-500/15 blur-lg" />
            <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-pink-500/15 blur-lg" />
          </div>

          <div className="max-w-md text-center">
            {/* Icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-linear-to-br from-purple-500 to-pink-500 shadow-[0_10px_25px_-5px_rgba(168,85,247,0.3)]">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 className="mb-2 text-3xl font-bold tracking-tight text-[#0a0a0a]">
              Something went wrong
            </h1>

            <p className="mb-8 leading-relaxed text-[#737373]">
              Oops! Our story hit an unexpected plot twist. Don&apos;t worry,
              every adventure has its bumps.
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={reset}
                className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-linear-to-br from-purple-500 to-pink-500 px-6 py-3 text-sm font-medium text-white shadow-[0_4px_14px_-3px_rgba(168,85,247,0.4)] transition-[opacity,transform] duration-200 hover:-translate-y-px hover:opacity-90"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
                Try Again
              </button>

              {/* global-error.tsx renders outside the Next.js router context,
                  so next/link is unavailable here. A plain <a> is correct.
                  eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <Link
                href="/"
                className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-6 py-3 text-sm font-medium text-[#0a0a0a] no-underline transition-colors duration-200 hover:bg-neutral-100"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Go Home
              </Link>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
